import {defineStore} from 'pinia'
import {AreaSection} from "../types/AreaSection";
import {AreaSectionPage} from "../types/AreaSectionPage";
import {ModelType} from "@spoferan/spoferan-ts-core";

const defaultAuthPermissions = {
    manage: false
}

export const useIndexStore = defineStore('index', {
    state: () => ({
        /**
         * The currently selected area object.
         */
        area: null as object | null,

        /**
         * The permissions of the authenticated user on the area.
         */
        authPermissions: {...defaultAuthPermissions} as { manage: boolean },

        isAuthInitialized: false as boolean,

        /**
         * The sections of the area to display in the nav.
         */
        areaSections: [] as AreaSection[],

        /**
         * Whether the guide's pages can be edited by the currently authenticated user. Can only be activated by admins with permission.
         */
        inEditingMode: false as boolean
    }),

    getters: {
        hasManagePermission: (state) => state.authPermissions.manage,
    },

    actions: {

        removePageFromAreaSection(page: AreaSectionPage) {
            const index = this.areaSections.findIndex(section => section.id === page.section_id)
            if (index > -1 && this.areaSections[index].pages && this.areaSections[index].pages.length) {

                // Delete page data on parent page
                if (page.parent_page_id) {
                    const parentIndex = this.areaSections[index].pages.findIndex(parentPage => parentPage.id === page.parent_page_id)
                    if (parentIndex > -1 && this.areaSections[index].pages[parentIndex].pages && this.areaSections[index].pages[parentIndex].pages.length) {
                        const pageIndex = this.areaSections[index].pages[parentIndex].pages.findIndex(subPage => subPage.id === page.id)
                        if (pageIndex > -1) {
                            this.areaSections[index].pages[parentIndex].pages.splice(pageIndex, 1)
                        }
                    }
                } else {
                    const pageIndex = this.areaSections[index].pages.findIndex(subPage => subPage.id === page.id)
                    if (pageIndex > -1) {
                        this.areaSections[index].pages.splice(pageIndex, 1)
                    }
                }

            }
        },

        /**
         * Loads the details of the section with the specified key from the server and sets it on the store.
         */
        async loadAreaSections(areaKey: string, forAdmins: boolean, cacheBusterTs: number|undefined = undefined): Promise<void> {
            try {
                const {$web, $app, $date, $apiFetch} = useNuxtApp();
                const {data} = await $apiFetch(`/v1/guide/${areaKey}/sections`, {
                    guest: !forAdmins,
                    params: {
                        only_published: !forAdmins,
                        ts: cacheBusterTs,
                        relations: ['pages']
                    }
                });

                // Prepend our static pages
                const sections = [
                    {
                        title: 'Allgemeines', static: true, is_published: true, pages: [
                            {title: 'Letzte Ankündigungen', is_published: true, static: true, to: $web('areas.changes.index', [areaKey])},
                            areaKey === 'spoferan' ? {title: 'FAQ', is_published: true, static: true, to: $web('areas.faq.index', 'spoferan')} : null
                        ].filter(page => !!page)
                    },
                    ...data
                ];

                this.areaSections = sections.map(section => {
                    return {
                        ...section, pages: section.pages && section.pages.length ? section.pages.map((page: AreaSectionPage) => {
                            if (page.static) {
                                return {...page};
                            }
                            return {
                                ...page,
                                to: $app('area-section-page', {params: {area: areaKey, section: section._key, page: page._key}, state: {page_ts: $date(page.updated_at).unix()}}),
                                pages: page.pages && page.pages.length ? page.pages.map(subPage => {
                                    return {...subPage, to: $app('area-section-page-sub_page', {params: {area: areaKey, section: section._key, page: page._key, sub_page: subPage._key}, state: {page_ts: $date(page.updated_at).unix()}})};
                                }) : []
                            };
                        }) : []
                    }
                })
            } catch (e) {
                throw e
            }
        },

        async refreshAuthPermissions() {
            const {$apiFetch, $auth} = useNuxtApp();

            if (!this.area || !this.area.id || !$auth.initialized) {
                this.authPermissions = {...defaultAuthPermissions};
                return;
            }

            const {data} = await $apiFetch('/v1/auth/permissions', {
                params: {
                    model_type: ModelType.GuideArea,
                    model_id: this.area.id,
                }
            });
            this.isAuthInitialized = true;
            this.authPermissions = data;
        },
    }
});